import {ConfigInfo, unwrapInterfaceId} from "#template/interface-descriptors"
import {Parameters} from "#template/nodes/parameters"
import type {AnyJsonValue} from "#template/value-types"
import {configurationParts, configuratorOrder, type VariationScope, variationName} from "#template/variations/configuration"
import type {Variation, VariationEvaluation, VariationOracle} from "#template/variations/gather-all-variations"
import {UnsupportedVariationStructureError, type VariationStructure} from "#template/variations/structure"

// Groups sharing a name are one choice to the user, so an entry can span several groups: it matches when at least one of
// them is present and every present one selects an allowed variant. A configuration matches when it matches every entry.
export type VariationFilterEntry = Readonly<Record<string, readonly string[]>>
export type VariationFilter = readonly VariationFilterEntry[]

export function matchesVariationFilter(configuration: Readonly<Record<string, unknown>>, filter: VariationFilter) {
    return filter.every((entry) => {
        const present = Object.keys(entry).filter((key) => key in configuration)
        return present.length > 0 && present.every((key) => entry[key].includes(configuration[key] as string))
    })
}

type AllowedVariants = Readonly<Record<string, readonly string[]>>

function allowedVariants(filter: VariationFilter): AllowedVariants {
    return Object.assign({}, ...filter)
}

export type FastVariationOptions<Extra> = {
    onVariation: (variation: Variation<Extra>) => void
    onConfigInfo?: (configInfo: ConfigInfo) => void
    filter?: VariationFilter
    // Each variation's `extra` comes from the compile of its gate context; this keeps render and post-processing settings in it exact.
    keepSceneSettingsExact?: boolean
    isAborted?: () => boolean
    yieldControl?: () => Promise<void>
    yieldEvery?: number
}

const defaultYield = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

type Assignment = Record<string, string>

export type ContextState<Extra> = {
    descriptors: VariationEvaluation<Extra>["descriptors"]
    extra: Extra
    configInfos: ConfigInfo[]
    byKey: Map<string, ConfigInfo>
}

// The default selection stays reachable even when its variant is excluded from permutations, as in the reference exploration.
export function permutableVariantIds(configInfo: ConfigInfo): string[] {
    const ids: string[] = configInfo.props.value ? [configInfo.props.value.id] : []
    for (const variant of configInfo.props.variants) {
        if (variant.excludeFromPermutations) continue
        if (!ids.includes(variant.id)) ids.push(variant.id)
    }
    return ids
}

function selectableVariantIds(configInfo: ConfigInfo, allowedByKey: AllowedVariants): string[] {
    const ids = permutableVariantIds(configInfo)
    const allowed = allowedByKey[configInfo.props.id]
    return allowed ? ids.filter((id) => allowed.includes(id)) : ids
}

export async function evaluateContext<Extra>(
    oracle: VariationOracle<Extra>,
    assignment: Assignment,
    onConfigInfo?: (configInfo: ConfigInfo) => void,
): Promise<ContextState<Extra>> {
    const {descriptors, extra} = await oracle.evaluate(assignment)
    const configInfos = descriptors.filter((d): d is ConfigInfo => d instanceof ConfigInfo)
    const byKey = new Map<string, ConfigInfo>()
    for (const configInfo of configInfos) {
        if (byKey.has(configInfo.props.id)) throw new UnsupportedVariationStructureError(`config group "${configInfo.props.id}" is active more than once`)
        byKey.set(configInfo.props.id, configInfo)
    }
    for (const [key, variantId] of Object.entries(assignment)) {
        if (byKey.get(key)?.props.value?.id !== variantId) throw new UnsupportedVariationStructureError(`selection of "${key}" did not take effect`)
    }
    if (onConfigInfo) configInfos.forEach(onConfigInfo)
    return {descriptors, extra, configInfos, byKey}
}

function isFreeKey(configInfo: ConfigInfo) {
    return !configInfo.props.isSetByTemplate && configInfo.props.value !== null
}

export function unassignedFreeKeys(state: ContextState<unknown>, assignment: Assignment, keyScope?: ReadonlySet<string>): string[] {
    return state.configInfos
        .filter((configInfo) => isFreeKey(configInfo) && !(configInfo.props.id in assignment) && (!keyScope || keyScope.has(configInfo.props.id)))
        .map((configInfo) => configInfo.props.id)
}

function isPresent(state: ContextState<unknown>, key: string) {
    const configInfo = state.byKey.get(key)
    return configInfo !== undefined && isFreeKey(configInfo)
}

export function choosableGate(structure: VariationStructure, gates: readonly string[]): string {
    const gate = gates.find((candidate) => {
        const controllers = structure.controllers(candidate)
        return !gates.some((other) => other !== candidate && controllers.has(other))
    })
    if (gate === undefined) throw new UnsupportedVariationStructureError(`config groups ${gates.join(", ")} control each other`)
    return gate
}

export function checkKeysAreKnown(structure: VariationStructure, keys: readonly string[]) {
    for (const key of keys) {
        if (!structure.isKnownKey(key)) throw new UnsupportedVariationStructureError(`config group "${key}" was not found by the analysis`)
    }
}

// Leaf selections never change which groups exist or how they evaluate, so one compile per combination of gate selections
// determines every configuration below it, including descriptor order and names.
export async function generateVariationsFast<Extra>(
    oracle: VariationOracle<Extra>,
    structure: VariationStructure,
    scope: VariationScope,
    options: FastVariationOptions<Extra>,
): Promise<void> {
    const {
        onVariation,
        onConfigInfo,
        filter = [],
        keepSceneSettingsExact = false,
        isAborted = () => false,
        yieldControl = defaultYield,
        yieldEvery = 200,
    } = options
    const isGate = (key: string) => structure.isGate(key) || (keepSceneSettingsExact && structure.affectsSceneSettings(key))
    const allowed = allowedVariants(filter)
    let sinceYield = 0

    const emitLeaves = async (state: ContextState<Extra>, assignment: Assignment, leaves: string[]) => {
        if (filter.some((entry) => !Object.keys(entry).some((key) => isPresent(state, key)))) return
        const domains = leaves.map((key) => selectableVariantIds(state.byKey.get(key)!, allowed))
        if (domains.some((domain) => domain.length === 0)) return

        const counters = leaves.map(() => 0)
        while (true) {
            if (isAborted()) return
            const selection: Assignment = {...assignment}
            leaves.forEach((key, index) => (selection[key] = domains[index][counters[index]]))
            onVariation(buildVariation(state, selection, scope))

            if (++sinceYield >= yieldEvery) {
                sinceYield = 0
                await yieldControl()
            }

            let position = leaves.length - 1
            while (position >= 0) {
                counters[position]++
                if (counters[position] < domains[position].length) break
                counters[position] = 0
                position--
            }
            if (position < 0) return
        }
    }

    const solve = async (assignment: Assignment): Promise<void> => {
        if (isAborted()) return
        const state = await evaluateContext(oracle, assignment, onConfigInfo)
        const free = unassignedFreeKeys(state, assignment)
        checkKeysAreKnown(structure, free)
        const gates = free.filter(isGate)

        if (gates.length === 0) {
            await emitLeaves(state, assignment, free)
            return
        }

        const gate = choosableGate(structure, gates)
        for (const variantId of selectableVariantIds(state.byKey.get(gate)!, allowed)) {
            if (isAborted()) return
            await solve({...assignment, [gate]: variantId})
        }
    }

    await solve({})
}

function buildVariation<Extra>(state: ContextState<Extra>, selection: Assignment, scope: VariationScope): Variation<Extra> {
    const configuration: {[paramId: string]: AnyJsonValue} = {}

    for (const descriptor of state.descriptors) {
        if (descriptor.props.type !== "input") continue
        if (!(descriptor instanceof ConfigInfo)) continue
        const {id, value, isSetByTemplate} = descriptor.props
        if (value === null) continue

        const variantId = isSetByTemplate ? value.id : (selection[id] ?? value.id)
        if (!isSetByTemplate || scope === "global") configuration[id] = variantId
    }

    const parts = configurationParts(state.configInfos, selection)
    const parameters = new Parameters({parameters: configuration})
    return {
        name: variationName(parts),
        parts,
        parameters,
        hash: parameters.getHash(),
        extra: state.extra,
    }
}

export type CountOptions = {
    filter?: VariationFilter
    isAborted?: () => boolean
    onConfigInfo?: (configInfo: ConfigInfo) => void
    // Estimated fraction done: every branch gets an equal share of its parent's range, since branch sizes are unknown in advance.
    onProgress?: (fraction: number) => void
}

// Gates whose influence sets are disjoint are counted independently, so their selections never need to be compiled together.
// That needs conditions on single groups, so entries spanning several groups are counted by inclusion–exclusion over the
// configurations in which all of their groups are absent.
export async function countVariationsFast(oracle: VariationOracle<unknown>, structure: VariationStructure, options: CountOptions = {}): Promise<bigint> {
    const {filter = [], isAborted = () => false, onConfigInfo, onProgress} = options
    const allowed = allowedVariants(filter)
    const required = filter.filter((entry) => Object.keys(entry).length === 1).map((entry) => Object.keys(entry)[0])
    const alternatives = filter.filter((entry) => Object.keys(entry).length !== 1).map((entry) => Object.keys(entry))
    let reported = 0
    const report = (fraction: number) => {
        if (!onProgress || fraction <= reported) return
        reported = fraction
        onProgress(fraction)
    }

    const count = async (
        absent: readonly string[],
        assignment: Assignment,
        keyScope: ReadonlySet<string> | undefined,
        from: number,
        to: number,
    ): Promise<bigint> => {
        const result = await countBranch(absent, assignment, keyScope, from, to)
        report(to)
        return result
    }

    const countBranch = async (
        absent: readonly string[],
        assignment: Assignment,
        keyScope: ReadonlySet<string> | undefined,
        from: number,
        to: number,
    ): Promise<bigint> => {
        if (isAborted()) throw new Error("Variation count aborted")
        const state = await evaluateContext(oracle, assignment, onConfigInfo)
        const free = unassignedFreeKeys(state, assignment, keyScope)
        checkKeysAreKnown(structure, free)
        const gates = free.filter((key) => structure.isGate(key))
        const leaves = free.filter((key) => !structure.isGate(key))

        // Presence that no open gate can change any more is final for every configuration from here.
        const settled = (key: string) => {
            if (keyScope && !keyScope.has(key)) return undefined
            const controllers = structure.controllers(key)
            return gates.some((gate) => controllers.has(gate)) ? undefined : key in assignment || isPresent(state, key)
        }
        if (required.some((key) => settled(key) === false) || absent.some((key) => settled(key) === true)) return 0n

        let total = 1n
        for (const leaf of leaves) {
            if (gates.some((gate) => structure.controllers(leaf).has(gate))) continue
            total *= BigInt(selectableVariantIds(state.byKey.get(leaf)!, allowed).length)
        }
        if (total === 0n) return 0n

        const components = groupByInfluence(structure, gates)
        const componentShare = (to - from) / Math.max(1, components.length)
        for (const [componentIndex, component] of components.entries()) {
            const gate = choosableGate(structure, component.gates)
            const componentScope = keyScope ? intersect(component.influence, keyScope) : component.influence
            const variantIds = selectableVariantIds(state.byKey.get(gate)!, allowed)
            const componentFrom = from + componentIndex * componentShare
            const variantShare = componentShare / Math.max(1, variantIds.length)
            let componentTotal = 0n
            for (const [variantIndex, variantId] of variantIds.entries()) {
                const variantFrom = componentFrom + variantIndex * variantShare
                componentTotal += await count(absent, {...assignment, [gate]: variantId}, componentScope, variantFrom, variantFrom + variantShare)
            }
            total *= componentTotal
            if (total === 0n) return 0n
        }
        return total
    }

    const subsets = 2 ** alternatives.length
    let total = 0n
    for (let subset = 0; subset < subsets; subset++) {
        const chosen = alternatives.filter((_, index) => subset & (2 ** index))
        const result = await count(chosen.flat(), {}, undefined, subset / subsets, (subset + 1) / subsets)
        total += chosen.length % 2 === 0 ? result : -result
    }
    return total
}

type Component = {gates: string[]; influence: Set<string>}

function groupByInfluence(structure: VariationStructure, gates: readonly string[]): Component[] {
    const components: Component[] = []
    for (const gate of gates) {
        const influence = new Set(structure.influence(gate))
        const merged: Component = {gates: [gate], influence}
        for (let index = components.length - 1; index >= 0; index--) {
            const component = components[index]
            if (!overlaps(component.influence, merged.influence)) continue
            merged.gates.unshift(...component.gates)
            for (const key of component.influence) merged.influence.add(key)
            components.splice(index, 1)
        }
        components.push(merged)
    }
    return components.map((component) => ({gates: gates.filter((gate) => component.gates.includes(gate)), influence: component.influence}))
}

function overlaps(a: ReadonlySet<string>, b: ReadonlySet<string>) {
    for (const item of a) if (b.has(item)) return true
    return false
}

function intersect(a: ReadonlySet<string>, b: ReadonlySet<string>) {
    const result = new Set<string>()
    for (const item of a) if (b.has(item)) result.add(item)
    return result
}

// Several consumers compile through the same scene manager, so evaluations must run one at a time; gate contexts repeat across
// counts and listings, so their results are kept.
export function cachedVariationOracle<Extra>(oracle: VariationOracle<Extra>, maxEntries = 512): VariationOracle<Extra> {
    const cache = new Map<string, Promise<VariationEvaluation<Extra>>>()
    let queue: Promise<unknown> = Promise.resolve()
    return {
        evaluate: (parameters) => {
            const key = JSON.stringify(Object.entries(parameters).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
            const cached = cache.get(key)
            if (cached) return cached
            const result = queue.then(() => oracle.evaluate(parameters))
            queue = result.catch(() => undefined)
            cache.set(key, result)
            result.catch(() => cache.delete(key))
            if (cache.size > maxEntries) cache.delete(cache.keys().next().value!)
            return result
        },
    }
}

export type VariationGroup = {
    key: string
    name: string
    section?: string
    variants: {id: string; name: string}[]
}

export type VariationFacetOption = {id: string; name: string; keys: string[]}
export type VariationFacet = {id: string; name: string; section?: string; keys: string[]; options: VariationFacetOption[]}

// Instances of a sub-template expose its groups under their own prefix, so groups are merged by their unprefixed id and
// options by variant id. Names come from the first appearance, since they can differ in spelling between sub-templates.
export function variationFacets(groups: readonly VariationGroup[]): VariationFacet[] {
    const facets = new Map<string, {name: string; section?: string; keys: string[]; options: Map<string, VariationFacetOption>}>()
    for (const group of groups) {
        const [, id] = unwrapInterfaceId(group.key)
        let facet = facets.get(id)
        if (!facet) {
            facet = {name: group.name, section: group.section, keys: [], options: new Map()}
            facets.set(id, facet)
        }
        facet.keys.push(group.key)
        for (const variant of group.variants) {
            let option = facet.options.get(variant.id)
            if (!option) {
                option = {id: variant.id, name: variant.name, keys: []}
                facet.options.set(variant.id, option)
            }
            option.keys.push(group.key)
        }
    }
    return [...facets].map(([id, {options, ...facet}]) => ({id, ...facet, options: [...options.values()]}))
}

// Every group of the facet is listed, so a present group without the option rules a configuration out.
export function facetFilterEntry(facet: VariationFacet, option: VariationFacetOption): VariationFilterEntry {
    return Object.fromEntries(facet.keys.map((key) => [key, option.keys.includes(key) ? [option.id] : []]))
}

export function withoutFacet(filter: VariationFilter, facet: VariationFacet): VariationFilter {
    return filter.filter((entry) => !Object.keys(entry).some((key) => facet.keys.includes(key)))
}

export class VariationCounter {
    private readonly groupsByKey = new Map<string, {configInfo: ConfigInfo; variants: Map<string, string>}>()

    constructor(
        private readonly oracle: VariationOracle<unknown>,
        private readonly structure: VariationStructure,
    ) {}

    count(filter: VariationFilter = [], isAborted?: () => boolean, onProgress?: (fraction: number) => void): Promise<bigint> {
        return countVariationsFast(this.oracle, this.structure, {filter, isAborted, onProgress, onConfigInfo: (configInfo) => this.recordGroup(configInfo)})
    }

    // Only complete once an unfiltered count has run.
    groups(): VariationGroup[] {
        return configuratorOrder(this.groupsByKey.values(), (entry) => entry.configInfo.props).map(({configInfo, variants}) => ({
            key: configInfo.props.id,
            name: configInfo.props.name,
            section: configInfo.props.sectionTitle,
            variants: [...variants].map(([id, name]) => ({id, name})),
        }))
    }

    // For every facet, keyed by id, how many configurations match if its own restriction is replaced by each of its options.
    async optionCounts(filter: VariationFilter, isAborted?: () => boolean): Promise<Map<string, Map<string, bigint>>> {
        const result = new Map<string, Map<string, bigint>>()
        for (const facet of variationFacets(this.groups())) {
            const others = withoutFacet(filter, facet)
            const counts = new Map<string, bigint>()
            for (const option of facet.options) counts.set(option.id, await this.count([...others, facetFilterEntry(facet, option)], isAborted))
            result.set(facet.id, counts)
        }
        return result
    }

    private recordGroup(configInfo: ConfigInfo) {
        if (!isFreeKey(configInfo)) return
        let entry = this.groupsByKey.get(configInfo.props.id)
        if (!entry) {
            entry = {configInfo, variants: new Map()}
            this.groupsByKey.set(configInfo.props.id, entry)
        }
        for (const variant of configInfo.props.variants) if (!entry.variants.has(variant.id)) entry.variants.set(variant.id, variant.name)
    }
}
